import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { workOrderProgress, STATUS_META, PART_STATUS_META, PART_STATUSES, derivePartStatus } from "@/lib/api";
import WorkOrderFormDialog from "@/components/WorkOrderFormDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Printer,
  User,
  Car,
  Wrench,
  Package,
  Calendar,
  FileText,
} from "lucide-react";

const STATUSES = [
  { value: "new", label: "New" },
  { value: "in_progress", label: "In Progress" },
  { value: "ready", label: "Ready" },
  { value: "completed", label: "Completed" },
];

export default function WorkOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [wo, setWo] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/work-orders/${id}`);
      setWo(data);
      const [c, v] = await Promise.all([
        api.get(`/customers/${data.customer_id}`).catch(() => ({ data: null })),
        api.get(`/vehicles/${data.vehicle_id}`).catch(() => ({ data: null })),
      ]);
      setCustomer(c.data);
      setVehicle(v.data);
    } catch (e) {
      toast.error("Repair order not found");
      navigate("/work-orders");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [id]);

  const changeStatus = async (status) => {
    try {
      const { data } = await api.patch(`/work-orders/${id}/status`, { status });
      setWo(data);
      toast.success(`Status → ${STATUS_META[status].label}`);
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const toggleLabor = async (idx, done) => {
    const labor_items = wo.labor_items.map((l, i) => (i === idx ? { ...l, done } : l));
    await savePartial({ labor_items });
  };

  const togglePart = async (idx, status) => {
    try {
      const { data } = await api.patch(
        `/work-orders/${id}/parts/${idx}/status`,
        { status }
      );
      setWo(data);
    } catch (e) {
      toast.error("Failed to update part status");
    }
  };

  const savePartial = async (patch) => {
    const payload = {
      customer_id: wo.customer_id,
      vehicle_id: wo.vehicle_id,
      complaint: wo.complaint || "",
      notes: wo.notes || "",
      status: wo.status,
      labor_items: wo.labor_items,
      parts_items: wo.parts_items,
      ...patch,
    };
    try {
      const { data } = await api.put(`/work-orders/${id}`, payload);
      setWo(data);
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const del = async () => {
    try {
      await api.delete(`/work-orders/${id}`);
      toast.success("Repair order deleted");
      navigate("/work-orders");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  if (loading || !wo) {
    return <div className="p-10 text-center text-slate-muted">Loading…</div>;
  }

  const prog = workOrderProgress(wo);
  const meta = STATUS_META[wo.status] || STATUS_META.new;

  return (
    <div>
      <div className="no-print">
        <Link
          to="/work-orders"
          className="inline-flex items-center gap-2 text-sm text-slate-muted hover:text-[#2B2D42] mb-4"
          data-testid="back-to-orders"
        >
          <ArrowLeft className="w-4 h-4" /> Back to repair orders
        </Link>
      </div>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8 no-print">
        <div>
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span className="font-mono text-sm font-semibold text-slate-muted">
              {wo.order_number}
            </span>
            <span
              className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${meta.cls}`}
              data-testid="wo-status-badge"
            >
              {meta.label}
            </span>
          </div>
          {vehicle?.license_plate ? (
            <div className="mb-3">
              <span
                className="inline-block font-mono text-xl md:text-2xl font-bold text-[#1A1A1A] bg-[#F4D35E]/40 border-2 border-[#E5A82E]/50 rounded-lg px-3 py-1"
                data-testid="wo-plate-label"
              >
                {vehicle.license_plate.toUpperCase()}
              </span>
            </div>
          ) : null}
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#2B2D42]">
            {vehicle ? `${vehicle.year || ""} ${vehicle.make} ${vehicle.model}`.trim() : "Repair Order"}
          </h1>
          <p className="text-slate-muted mt-1">
            {customer?.name || "—"} · Created {new Date(wo.created_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={wo.status} onValueChange={changeStatus}>
            <SelectTrigger
              data-testid="change-status-select"
              className="w-40 rounded-xl border-2 border-warm bg-white"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              {STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => window.print()}
            className="rounded-xl border-2 border-warm"
            data-testid="print-wo-btn"
          >
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
          <Button
            variant="outline"
            onClick={() => setEditOpen(true)}
            className="rounded-xl border-2 border-warm"
            data-testid="edit-wo-btn"
          >
            <Pencil className="w-4 h-4 mr-2" /> Edit
          </Button>
          <Button
            variant="outline"
            onClick={() => setConfirmDelete(true)}
            className="rounded-xl border-2 border-warm text-[#E07A5F] hover:bg-[#FAE8E4]"
            data-testid="delete-wo-btn"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Card className="rounded-2xl border-warm shadow-card p-8 mb-6 bg-white" data-testid="wo-sheet">
        {/* Print header */}
        <div className="hidden print:block mb-6">
          <div className="flex items-center justify-between border-b border-slate-300 pb-4">
            <div>
              <h2 className="font-heading text-2xl font-bold">Car Dream</h2>
              <p className="text-sm text-slate-500">Auto · Repair · Paint</p>
            </div>
            <div className="text-right">
              <p className="font-mono font-semibold">{wo.order_number}</p>
              <p className="text-sm text-slate-500">
                {new Date(wo.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="p-5 rounded-xl bg-[#FAF9F6] border border-warm">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-muted font-semibold mb-2">
              <User className="w-3.5 h-3.5" /> Customer
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-heading text-lg font-semibold text-[#2B2D42]">
                {customer?.name || "—"}
              </div>
              {customer?.type === "dealer" && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider bg-[#F4D35E]/30 text-[#8B6914] border border-[#E5A82E]/40"
                  data-testid="wo-customer-type-badge"
                >
                  Dealer
                </span>
              )}
            </div>
            {customer?.phone && (
              <div className="text-sm text-slate-muted">{customer.phone}</div>
            )}
            {customer?.email && (
              <div className="text-sm text-slate-muted">{customer.email}</div>
            )}
          </div>
          <div className="p-5 rounded-xl bg-[#FAF9F6] border border-warm">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-muted font-semibold mb-2">
              <Car className="w-3.5 h-3.5" /> Vehicle Reg #
            </div>
            {vehicle?.license_plate ? (
              <div className="font-mono text-xl font-bold text-[#1A1A1A] bg-[#F4D35E]/40 border-2 border-[#E5A82E]/50 rounded-lg px-3 py-1 inline-block mb-2">
                {vehicle.license_plate.toUpperCase()}
              </div>
            ) : (
              <div className="text-slate-muted italic mb-2">Not registered</div>
            )}
            <div className="font-heading text-base font-semibold text-[#2B2D42]">
              {vehicle ? `${vehicle.year || ""} ${vehicle.make} ${vehicle.model}`.trim() : "—"}
            </div>
            {vehicle?.mileage ? (
              <div className="text-sm text-slate-muted">
                {vehicle.mileage.toLocaleString()} mi
              </div>
            ) : null}
          </div>
        </div>

        {wo.complaint && (
          <div className="mb-8">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-muted font-semibold mb-2">
              <FileText className="w-3.5 h-3.5" /> Customer Complaint
            </div>
            <p className="text-[#2B2D42] whitespace-pre-wrap">{wo.complaint}</p>
          </div>
        )}

        {/* Progress bar */}
        {prog.total > 0 && (
          <div className="mb-8 no-print" data-testid="wo-progress">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-widest text-slate-muted font-semibold">
                Progress
              </span>
              <span className="text-sm font-medium text-[#2B2D42]">
                {prog.done} of {prog.total} · {prog.pct}%
              </span>
            </div>
            <Progress value={prog.pct} className="h-2 bg-[#EAE8E3]" />
          </div>
        )}

        {/* Repair tasks */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-muted font-semibold mb-3">
            <Wrench className="w-3.5 h-3.5" /> Repair Tasks
            <span className="ml-auto text-slate-muted normal-case tracking-normal">
              {prog.laborDone}/{prog.laborTotal} done
            </span>
          </div>
          {wo.labor_items.length === 0 ? (
            <p className="text-sm text-slate-muted italic">No repair tasks listed</p>
          ) : (
            <div className="rounded-xl overflow-hidden border border-warm">
              {wo.labor_items.map((l, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-3 px-4 py-3 border-b border-warm last:border-b-0 ${
                    l.done ? "bg-[#EAF4F0]" : "bg-white"
                  }`}
                  data-testid={`labor-item-${i}`}
                >
                  <Checkbox
                    checked={l.done}
                    onCheckedChange={(v) => toggleLabor(i, !!v)}
                    data-testid={`toggle-labor-${i}`}
                  />
                  <span
                    className={`flex-1 text-[#2B2D42] ${
                      l.done ? "line-through text-slate-muted" : ""
                    }`}
                  >
                    {l.description || "Task"}
                  </span>
                  {l.hours ? (
                    <span className="text-xs text-slate-muted font-mono">
                      ~{l.hours}h
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Parts to order */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-muted font-semibold mb-3">
            <Package className="w-3.5 h-3.5" /> Parts to Order
            <span className="ml-auto text-slate-muted normal-case tracking-normal">
              {prog.partsOrdered}/{prog.partsTotal} ordered
            </span>
          </div>
          {wo.parts_items.length === 0 ? (
            <p className="text-sm text-slate-muted italic">No parts needed</p>
          ) : (
            <div className="rounded-xl overflow-hidden border border-warm">
              {wo.parts_items.map((p, i) => {
                const lineStatus = derivePartStatus(p);
                const meta = PART_STATUS_META[lineStatus] || PART_STATUS_META.pending;
                return (
                  <div
                    key={i}
                    className={`flex items-center gap-3 px-4 py-3 border-b border-warm last:border-b-0 ${
                      lineStatus === "received" ? "bg-[#EAF4F0]" : "bg-white"
                    }`}
                    data-testid={`part-item-${i}`}
                  >
                    <span
                      className={`flex-1 text-[#2B2D42] ${
                        lineStatus === "received" ? "line-through text-slate-muted" : ""
                      }`}
                    >
                      {p.name}
                    </span>
                    <span className="text-sm font-medium text-[#3D405B]">
                      × {p.quantity}
                    </span>
                    <Select
                      value={lineStatus}
                      onValueChange={(val) => togglePart(i, val)}
                    >
                      <SelectTrigger
                        data-testid={`part-status-${i}`}
                        className={`h-auto py-1 px-2.5 rounded-full text-xs font-semibold uppercase tracking-wider border w-auto gap-1.5 focus:ring-2 focus:ring-[#E5A82E] [&>svg]:h-3 [&>svg]:w-3 [&>svg]:opacity-70 ${meta.cls}`}
                      >
                        <span>{meta.label}</span>
                      </SelectTrigger>
                      <SelectContent className="rounded-xl">
                        {PART_STATUSES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {wo.notes && (
          <div className="mt-8 p-4 rounded-xl bg-[#FCF5E8] border border-[#EBD9A8] no-print">
            <div className="text-xs uppercase tracking-widest text-[#8B6914] font-semibold mb-1">
              Internal Notes
            </div>
            <p className="text-[#2B2D42] text-sm whitespace-pre-wrap">{wo.notes}</p>
          </div>
        )}

        <div className="mt-6 flex items-center gap-2 text-xs text-slate-muted no-print">
          <Calendar className="w-3.5 h-3.5" />
          Created {new Date(wo.created_at).toLocaleString()}
          {wo.completed_at && (
            <span className="ml-4">
              Completed {new Date(wo.completed_at).toLocaleString()}
            </span>
          )}
        </div>
      </Card>

      <WorkOrderFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        editing={wo}
        onSaved={load}
      />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete this repair order?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={del}
              className="bg-[#E07A5F] hover:bg-[#C86A4F] text-white rounded-xl"
              data-testid="confirm-delete-wo"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
